import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Tag } from 'antd';
import { Button } from 'components/Button';
import { getWarehouseDetailApi, getWarehouseStockApi } from 'api/services/uniformManagement';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import useStickyIdentity from '../../components/useStickyIdentity';

const WarehouseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [warehouse, setWarehouse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });

  const fetchWarehouse = () => {
    setLoading(true);
    getWarehouseDetailApi(id).then(response => {
      setWarehouse(response.data?.data || null);
      setLoading(false);
    }).catch(() => { setLoading(false); navigate('/uniform-management'); });
  };

  const fetchStock = useCallback(() => {
    setStockLoading(true);
    getWarehouseStockApi(id, {
      page: pageSettings.page,
      page_size: pageSettings.pageSize,
    }).then(response => {
      setStockData(response.data?.results || []);
      setPageSettings(prev => ({ ...prev, total: response.data?.count || 0 }));
      setStockLoading(false);
    }).catch(() => setStockLoading(false));
  }, [id, pageSettings.page, pageSettings.pageSize]);

  useEffect(() => {
    fetchWarehouse();
  }, [id]);

  useEffect(() => {
    fetchStock();
  }, [fetchStock]);

  const handleChange = (pagination, filters, sorter) => {
    let pageData = handlePageChange(pagination, filters, sorter, pageSettings);
    setPageSettings(pageData);
  };

  // Above the loading guard below: a hook cannot sit after an early return.
  const pinLeft = useStickyIdentity();

  if (loading || !warehouse) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="text-center py-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
        </div>
      </div>
    );
  }

  const stockColumns = [
    // Pinned: which item a quantity belongs to is the whole point of the row.
    { title: 'Uniform Item', dataIndex: 'uniform_item_name', ...pinLeft(220) },
    { title: 'Size', dataIndex: 'size_name', render: (name, record) => `${name} (${record.size_code})` },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      sorter: true,
      render: (qty, record) => {
        const isLow = record.reorder_level > 0 && qty <= record.reorder_level;
        return <span style={{ color: isLow ? '#ff4d4f' : 'inherit', fontWeight: isLow ? 600 : 400 }}>{qty}</span>;
      }
    },
    {
      title: 'Reorder Level',
      dataIndex: 'reorder_level',
      render: (val) => val || '-'
    },
    {
      title: 'Stock Status',
      render: (_, record) => {
        if (!record.reorder_level || record.reorder_level === 0) return <Tag>N/A</Tag>;
        if (record.quantity <= record.reorder_level) return <Tag color="red">Low Stock</Tag>;
        return <Tag color="green">OK</Tag>;
      }
    }
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
<h3 className="d-inline">{warehouse.name}</h3>
            <span className="text-muted ms-2">({warehouse.code})</span>
            {warehouse.is_main && <Tag color="blue" className="ms-2">Main</Tag>}
            {warehouse.can_issue_uniform && <Tag color="green" className="ms-2">Issues uniforms</Tag>}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-4 mb-3">
                <strong>Address:</strong> <span className="ms-2">{warehouse.address || '-'}</span>
              </div>
              <div className="col-md-4 mb-3">
                <strong>Contact Person:</strong> <span className="ms-2">{warehouse.contact_person || '-'}</span>
              </div>
              <div className="col-md-4 mb-3">
                <strong>Contact Number:</strong> <span className="ms-2">{warehouse.contact_number || '-'}</span>
              </div>
              <div className="col-md-4 mb-3">
                <strong>Status:</strong>
                <span className={`badge ms-2 ${warehouse.status ? 'bg-success-light' : 'bg-danger-light'}`}>
                  {warehouse.status ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Stock Levels</h5>
          </div>
          <div className="card-body">
            <Table
              columns={stockColumns}
              dataSource={stockData}
              rowKey={(record) => record.id}
              loading={stockLoading}
              pagination={{
                total: pageSettings.total,
                pageSize: pageSettings.pageSize,
                current: pageSettings.page,
                pageSizeOptions,
                itemRender,
                showSizeChanger: true
              }}
              onChange={handleChange}
              scroll={{ x: "max-content" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarehouseDetail;
